import { z } from "zod";
import { COLLECTION_STATUS } from "./collections.js";

/* ===========================================================================
 * US-E24.F2-0351 — VÒNG ĐỜI ĐỢT THU: tạo (đúng MỘT lần) → sống → đóng/huỷ
 * (@UC9564 @UC9565 @UC9566 @UC9567 — @gate money).
 *
 * Repo trước task này chỉ có "tạo". Không đường huỷ, không đường đóng ⇒ một đợt thu tạo nhầm để lại
 * `member_dues` `pending` treo VĨNH VIỄN cho cả nhóm, và cách "sửa" duy nhất là bảo mọi người bỏ qua
 * đợt thu ma. Đó chính là thứ phá niềm tin mà Goppy tồn tại để bảo vệ.
 *
 * 🔴 QĐ PO 06/08/2026 (index §7) — hai đường TÁCH BẠCH, không nhập một:
 *  - **Huỷ** (`DELETE`): CHỈ khi CHƯA AI ĐÓNG. Đợt + mọi due chưa đóng biến mất trong CÙNG 1 tx.
 *    Có dù chỉ một người đã chạm tiền ⇒ **409 fail-closed** + hướng sang "đóng đợt".
 *  - **Đóng** (`PATCH .../close`): `status='closed'`. Dues giữ NGUYÊN bit-for-bit. Đây là đường
 *    thoát ĐÚNG cho thủ quỹ tạo nhầm một đợt đã có người đóng.
 *
 * 🔴 0 BÚT TOÁN ở CẢ HAI đường. Huỷ ≠ hoàn tiền: tiền đã vào quỹ là FACT ngoài đời — đảo bút toán
 * không làm tiền quay về ví ai, chỉ làm sổ lệch thực tế (§3.2/§3.3; QĐ PO Q1).
 * ========================================================================= */

/**
 * 409 — tạo đợt thu TRÙNG NỘI DUNG (@UC9564, S01). `code` ổn định, ngôn-ngữ-trung-tính; client map
 * `code → message` và gợi ý người dùng đổi tiêu đề nếu thật sự muốn hai đợt riêng.
 */
export const COLLECTION_DUPLICATE_CREATE_CODE = "COLLECTION_DUPLICATE_CREATE" as const;

/**
 * 422 — `memberIds` có người **thuộc nhóm nhưng KHÔNG `active`** (@UC9713, US-E24.F33-0447).
 * Client map → *"Có người trong danh sách đã rời nhóm — bỏ họ ra rồi tạo lại."*
 *
 * 🔴 TÁCH KHỎI 404 CÓ CHỦ ĐÍCH. 404 dùng cho id **không thuộc nhóm** (mirror `FundAccessGuard`:
 * không phân biệt "không tồn tại" với "không thuộc nhóm bạn" ⇒ không làm oracle dò id). Còn "đã rời
 * nhóm" là tình huống **có thật, sửa được**, và caller đã là OWNER/ADMIN/TREASURER — họ đọc được
 * danh sách thành viên rồi, nên vế này KHÔNG lộ thêm gì. Gộp cả hai vào 404 chỉ khiến thủ quỹ đi tìm
 * một thành viên "không tồn tại" mà họ vừa nhìn thấy trên màn hình.
 *
 * ⚠️ Đường LỊCH ĐỊNH KỲ (@UC9616) **cố ý KHÔNG có** mã này: phạm vi lịch neo `groupMemberId` chứ
 * không neo `status`, vì còn *kỳ sau* để người tạm nghỉ quay lại. Đợt MỘT LẦN không có kỳ sau — due
 * sinh ngay lúc bấm — nên chọn người đã rời nhóm là đòi tiền một người không còn ở đó.
 */
export const COLLECTION_MEMBER_NOT_ACTIVE_CODE = "COLLECTION_MEMBER_NOT_ACTIVE" as const;

/**
 * 409 — không huỷ được vì ĐÃ CÓ NGƯỜI CHẠM TIỀN (@UC9565/@UC9566, S02).
 * Client map → "Đợt này đã có người đóng nên không xoá được. Bạn có thể **đóng đợt** để dừng thu."
 */
export const COLLECTION_HAS_PAID_DUES_CODE = "COLLECTION_HAS_PAID_DUES" as const;

/**
 * 409 — đợt thu thuộc một VÒNG ĐỜI KHÁC, endpoint này không được phép đụng vào (REVIEW C1/H3).
 *
 * 🔴 Vì sao fail-closed theo `kind` chứ không "cho chạy hết cho tiện":
 *
 * **`settlement`** — bất biến *"1 settlement OPEN / quỹ"* (`settlement-collection.service.ts`) được
 * cài bằng `findFirst({kind:'settlement', status:'open'})`. Trước task này nó **tuyệt đối**, vì
 * `closed` không có luồng nghiệp vụ nào đặt được. Nếu `close()` đóng được một settlement đang mở thì
 * lần tất toán sau **không thấy đợt cũ nữa ⇒ tạo đợt thứ hai**, mà `computeSettlementOwed`
 * (`financial-position.service.ts`) cộng **MỌI** due chưa đóng của quỹ **không lọc `kind`** ⇒ due mới
 * = nợ gốc + due tất toán cũ ⇒ **đòi 2× rồi 3×, cộng dồn mỗi vòng**. Và kịch bản kích hoạt lại đúng
 * là thứ ta khuyên người dùng làm ("đã có người đóng ⇒ hãy ĐÓNG đợt").
 *
 * **`periodic`** — chỉ chặn ở `cancel()`. Đợt định kỳ do `collection_schedules` sinh ra và có vòng
 * đời riêng (pause/resume/cancel/reprice/skip). Xoá hàng `collection` chỉ xoá luôn khoá
 * `one_collection_per_schedule_period` ⇒ lần cron kế tiếp **sinh lại y hệt** ⇒ "đã huỷ" là một lời
 * nói dối có hạn dùng vài giờ. Muốn dừng thu định kỳ thì dừng LỊCH, không xoá một kỳ.
 * `close()` vẫn cho phép với `periodic` — đó chính là consumer mà S03 hồi sinh
 * (`prorata-due.producer.ts` chặn tính lại pro-rata trên kỳ đã đóng sổ).
 *
 * §2 index.md đã viết sẵn nguyên tắc này cho `collection_schedules`: *"đó là cơ chế khác, đừng nhập
 * hai vòng đời làm một"*. C1 là đúng cái giá của việc quên áp nó cho `settlement`.
 */
export const COLLECTION_NOT_CANCELABLE_KIND_CODE = "COLLECTION_NOT_CANCELABLE_KIND" as const;

/** 409 — `PATCH .../close` trên đợt thuộc vòng đời khác (hiện tại: `settlement`). Xem code trên. */
export const COLLECTION_NOT_CLOSABLE_KIND_CODE = "COLLECTION_NOT_CLOSABLE_KIND" as const;

/** Trạng thái vòng đời hợp lệ mà `PATCH` được phép đặt (chỉ `closed` — mở lại đợt KHÔNG thuộc task này). */
export const closeCollectionRequestSchema = z
  .object({
    status: z.literal(COLLECTION_STATUS.CLOSED),
  })
  .strict();

export type CloseCollectionRequest = z.infer<typeof closeCollectionRequestSchema>;

/**
 * Kết quả một thao tác vòng đời.
 *  - `collectionId` — đợt bị tác động.
 *  - `outcome` — `cancelled` (đợt bị xoá hẳn) | `closed` (đợt còn, chuyển `closed`) |
 *    `already_closed` (đóng lại một đợt đã đóng — vô hại, idempotent).
 *  - `duesRemoved` — số due bị xoá cùng đợt (luôn `0` ở nhánh `closed`/`already_closed`).
 *  - `collectionStatus` — trạng thái ĐỢT sau thao tác; `null` khi đợt đã bị xoá.
 *
 * 🔴 KHÔNG trả bất kỳ số tiền nào: endpoint này không đụng tiền, và một con số tiền trong response
 * của một thao tác không-tiền chỉ tạo ảo giác rằng nó có.
 */
export const COLLECTION_LIFECYCLE_OUTCOME = {
  CANCELLED: "cancelled",
  CLOSED: "closed",
  ALREADY_CLOSED: "already_closed",
} as const;

export type CollectionLifecycleOutcome =
  (typeof COLLECTION_LIFECYCLE_OUTCOME)[keyof typeof COLLECTION_LIFECYCLE_OUTCOME];

export const collectionLifecycleResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    outcome: z.enum([
      COLLECTION_LIFECYCLE_OUTCOME.CANCELLED,
      COLLECTION_LIFECYCLE_OUTCOME.CLOSED,
      COLLECTION_LIFECYCLE_OUTCOME.ALREADY_CLOSED,
    ]),
    duesRemoved: z.number().int().min(0),
    collectionStatus: z
      .enum([COLLECTION_STATUS.OPEN, COLLECTION_STATUS.CLOSED])
      .nullable(),
  })
  .strict();

export type CollectionLifecycleResponse = z.infer<typeof collectionLifecycleResponseSchema>;

/** Outbox event khi một đợt thu bị HUỶ — nhóm đã được báo "có đợt thu mới", phải được báo nó không còn. */
export const COLLECTION_CANCELLED_EVENT = "collection.cancelled" as const;
